import { useState, type JSX } from "react";
import type { ResolvedDecision } from "@magnis/client-core";
import type { AppRuntime } from "../../runtime/contracts/runtime";
import { ExpandableEntityCard } from "./ExpandableEntityCard";
import { extractEntities } from "./AgentPanelBlocks";
import { Icon } from "../ui/Icon";

export interface DecisionSummaryProps {
  readonly decision: ResolvedDecision;
  /** Passed through to EntityCardRenderer when the result carries an entity. */
  readonly runtime?: AppRuntime;
}

function capitalize(s: string): string {
  const first = s.charAt(0);
  return first ? first.toUpperCase() + s.slice(1) : s;
}

/**
 * Extract just the verb from a tool name for use as an action chip prefix
 * on the embedded entity card. "email.send" → "Send", "notes.create" →
 * "Create", "email.batch_send" → "Send" (drops the `batch` qualifier),
 * "email.set_trigger" → "Set trigger".
 */
function verbFromToolName(toolName: string): string | undefined {
  const parts = toolName.split(/[._]/).filter(Boolean);
  if (parts.length === 0) return undefined;
  // Drop the module prefix (first token: "email", "notes", "contacts").
  const tail = parts.length > 1 ? parts.slice(1) : parts;
  // For "batch_send" → use only the trailing verb.
  const filtered = tail[0] === "batch" && tail.length > 1 ? tail.slice(1) : tail;
  return filtered.map((p, i) => (i === 0 ? capitalize(p) : p)).join(" ");
}

/**
 * Turn `contacts.create` / `email.batch_send` / `notes_create` into a
 * readable verb-first title like `Create contacts` / `Send email batch` /
 * `Create notes`. When args carry a natural label (`name`, `title`,
 * `subject`), append it so the summary reads like a sentence —
 * `Create contacts: Вася`.
 */
function summaryTitle(toolName: string, args: unknown): string {
  const parts = toolName.split(/[._]/).filter(Boolean);
  const action = parts.length > 1 ? (parts[parts.length - 1] ?? toolName) : toolName;
  const rest = parts.length > 1 ? parts.slice(0, -1).join(" ") : "";
  const base = rest ? `${capitalize(action)} ${rest}` : capitalize(action);

  if (args !== null && typeof args === "object") {
    const o = args as Record<string, unknown>;
    // Surface the user-readable label so the row shows WHAT was acted
    // on, not just the tool name. `query` covers search.* tools where
    // the prompt is the most informative thing about the call.
    for (const key of ["name", "title", "subject", "query"] as const) {
      const v = o[key];
      if (typeof v === "string" && v.length > 0) return `${base}: ${v}`;
    }
  }
  return base;
}

/**
 * Render the summary title with bold verb + dim tail, split on the
 * first colon. "Hybrid search: Кто в нас инвестировал?" →
 * **Hybrid search**: Кто в нас инвестировал?
 */
function TitleParts({ title }: { readonly title: string }): JSX.Element {
  const colonIdx = title.indexOf(":");
  if (colonIdx <= 0) {
    return <span className="font-bold">{title}</span>;
  }
  const verb = title.slice(0, colonIdx);
  const rest = title.slice(colonIdx + 1).trimStart();
  return (
    <>
      <span className="font-bold">{verb}</span>
      <span className="text-agent-text-muted">: {rest}</span>
    </>
  );
}

function isRenderable(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.length > 0;
  if (typeof v === "number" || typeof v === "boolean") return true;
  return Array.isArray(v) || typeof v === "object";
}

function formatValue(v: unknown): string {
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join(", ");
  return JSON.stringify(v);
}

function ArgsTable({ args }: { readonly args: unknown }): JSX.Element | null {
  if (args === null || typeof args !== "object") return null;
  const entries = Object.entries(args as Record<string, unknown>).filter(([, v]) => isRenderable(v));
  if (entries.length === 0) return null;
  return (
    <dl className="space-y-1 text-[12px] leading-[1.5]">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-2">
          <dt className="text-agent-text-dim w-24 shrink-0">{key}</dt>
          <dd className="text-agent-text break-words min-w-0 flex-1">{formatValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Compact read-only "Answers" block for a resolved tool call.
 *
 * Always renders as a collapsed summary line ("✓ Create contacts: Вася")
 * with a chevron. Expanding reveals either one EntityCard per resolved
 * entity (preferred) or a neutral args table when the result is not
 * entity-shaped (denied decisions, background calls without a visible
 * result).
 */
export function DecisionSummary({ decision, runtime }: DecisionSummaryProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  if (decision.kind === "approval") {
    const isApproved = decision.status === "approved";
    const title = summaryTitle(decision.toolName, decision.args);

    // Resolve the expanded body once. Prefer EntityCard(s) when available,
    // fall back to the args table so denied / non-entity approvals still
    // offer something to inspect.
    const entities =
      isApproved && runtime
        ? extractEntities(decision.result, { toolName: decision.toolName })
        : [];
    const hasEntities = entities.length > 0;

    // SINGLE-ENTITY APPROVAL — collapse the whole decision into the
    // entity card itself. The "Send: subject" verb prefix is rendered
    // inline by the card via `action`. One chevron, one component,
    // no extra summary row. See docs/frontend/module-standard.md
    // ("ONE COMPONENT PER ENTITY").
    if (isApproved && runtime && entities.length === 1) {
      const entity = entities[0];
      const schemaId = entity?.schema_id;
      if (entity && typeof schemaId === "string" && schemaId.length > 0) {
        return (
          <ExpandableEntityCard
            schemaId={schemaId}
            data={entity}
            runtime={runtime}
            action={verbFromToolName(decision.toolName)}
          />
        );
      }
    }
    const hasArgs =
      decision.args !== null &&
      typeof decision.args === "object" &&
      Object.values(decision.args as Record<string, unknown>).some(isRenderable);
    const canExpand = hasEntities || hasArgs;

    return (
      <div
        data-testid={`decision-approval-${decision.toolCallId}`}
        className="rounded-lg border border-agent-border bg-surface-secondary overflow-hidden"
      >
        <button
          type="button"
          onClick={() => { if (canExpand) setExpanded(!expanded); }}
          disabled={!canExpand}
          className={`w-full flex items-center gap-2 px-3 py-2 text-[12px] text-left ${canExpand ? "hover:bg-surface-hover cursor-pointer" : "cursor-default"}`}
        >
          <Icon
            name={isApproved ? "circle-check" : "close"}
            size={14}
            className={isApproved ? "text-emerald-400" : "text-red-400"}
          />
          <span className="flex-1 truncate text-agent-text">
            <TitleParts title={title} />
          </span>
          {canExpand && (
            <Icon
              name={expanded ? "chevron-down" : "chevron-right"}
              size={14}
              className="text-agent-text-dim"
            />
          )}
        </button>
        {canExpand && expanded && (
          <div
            className="border-t border-agent-border px-3 py-2"
            data-testid={`decision-approval-${decision.toolCallId}-expanded`}
          >
            {hasEntities && runtime ? (
              <div className="space-y-1">
                {entities.map((entity, i) => {
                  const schemaId = entity.schema_id as string;
                  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  const key = (entity.id as string | undefined) ?? `entity-${i}`;
                  return (
                    <ExpandableEntityCard
                      key={key}
                      schemaId={schemaId}
                      data={entity}
                      runtime={runtime}
                    />
                  );
                })}
              </div>
            ) : (
              <ArgsTable args={decision.args} />
            )}
          </div>
        )}
      </div>
    );
  }

  // ask_user — static Q/A block (no entity to promote here).
  return (
    <div
      data-testid={`decision-ask_user-${decision.toolCallId}`}
      className="rounded-lg border border-agent-border bg-surface-secondary px-3 py-2.5"
    >
      <div className="flex items-center gap-1.5 text-[12px] font-semibold text-agent-text-muted mb-1.5">
        <Icon name="circle-check" size={12} className="text-emerald-400" />
        Answers
      </div>
      <dl className="space-y-1.5 text-[12px] leading-[1.45]">
        {decision.answers.map((qa, i) => (
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          <div key={`qa-${i}`}>
            {qa.question !== undefined && (
              <dt className="text-agent-text-dim">{qa.question}</dt>
            )}
            <dd className="text-agent-text">{qa.answer}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
